/**
 * The WordPress card of a Saidiseire site page: the inside view the reporter
 * plugin sends (docs/saidiseire-plaan.md, item F).
 *
 * Four states, one card: no token yet (say what the plugin is and offer a
 * token), a token that has not reported (say where to paste it), a link gone
 * quiet for two days (say so in red, keep the last report visible), and a
 * link that reports (the summary and the tabs).
 *
 * Creating, replacing and revoking a token answer to `manage:crm.monitoring`;
 * reading is `view:crm` like the rest of the page. A new token is shown once
 * in `TokenDialog` and never again.
 *
 * A token is issued only for a site whose web maintenance package includes the
 * reporter (`allowed`, docs/saidiseire-paketid.md). Without it the card says
 * why instead of offering a button the server would refuse; a key issued before
 * the package dropped it keeps working, can be revoked, and is not replaced.
 */
import { useState } from "react";
import { Blocks, KeyRound, Loader2, Unplug } from "lucide-react";
import {
  issueReporterToken,
  linkTone,
  reporterErrorKey,
  reporterLinkState,
  revokeReporter,
  type ReporterDetailResponse,
} from "../../../../lib/monitoringReporter";
import { NO_EXPECT } from "../../../../lib/monitoringBackup";
import { Btn, CardHead, StatusPill, fmtDateTime, useToast } from "../../../ui";
import { ConfirmModal } from "../../_shared/ConfirmModal";
import type { T } from "../words";
import { ReportSummary } from "./ReportSummary";
import { ReportTabs } from "./ReportTabs";
import { TokenDialog } from "./TokenDialog";
import { useReporter } from "./useReporter";

function stateBody(t: T, data: ReporterDetailResponse): string | null {
  const state = reporterLinkState(data);
  const link = data.link;
  if (state === "none") return t("monitoring.reporter.noneBody");
  if (!link) return null;
  if (state === "waiting") return t("monitoring.reporter.waitingBody", { key: link.keyHint });
  if (state === "silent") {
    return link.lastReportAt && link.lastReportAt >= link.issuedAt
      ? t("monitoring.reporter.silentBody", { when: fmtDateTime(link.lastReportAt) })
      : t("monitoring.reporter.silentNever", { when: fmtDateTime(link.issuedAt) });
  }
  return null;
}

export function ReporterCard({
  monitorId,
  canManage,
  allowed,
  t,
}: {
  monitorId: string;
  canManage: boolean;
  /** The site's package includes the reporter (`MonitorPolicyView.wpReporter`). */
  allowed: boolean;
  t: T;
}) {
  const { data, failed, reload } = useReporter(monitorId);
  const toast = useToast();
  const [token, setToken] = useState<string | null>(null);
  const [asking, setAsking] = useState<"rotate" | "revoke" | null>(null);
  const [busy, setBusy] = useState(false);

  async function issue() {
    setBusy(true);
    try {
      const res = await issueReporterToken(monitorId);
      setToken(res.token);
      void reload();
    } catch (err) {
      toast(t(`monitoring.reporter.error.${reporterErrorKey(err)}`));
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    try {
      await revokeReporter(monitorId);
      void reload();
    } catch (err) {
      toast(t(`monitoring.reporter.error.${reporterErrorKey(err)}`));
    }
  }

  const title = (
    <span className="inline-flex items-center gap-2">
      <Blocks className="size-4 text-muted-foreground" strokeWidth={1.8} />
      {t("monitoring.reporter.title")}
    </span>
  );

  if (!data) {
    return (
      <section className="crm-card mb-4">
        <CardHead title={title} />
        <div className="flex justify-center px-[26px] pb-6 text-muted-foreground">
          {failed ? (
            <div className="text-center">
              <p className="mb-3 text-[13px] text-[var(--color-destructive)]">{t("monitoring.reporter.loadFailed")}</p>
              <Btn small onClick={() => void reload()}>
                {t("monitoring.retry")}
              </Btn>
            </div>
          ) : (
            <Loader2 className="size-5 animate-spin" />
          )}
        </div>
      </section>
    );
  }

  const state = reporterLinkState(data);
  const body = !allowed && state === "none" ? t("monitoring.reporter.notInPlan") : stateBody(t, data);
  const report = data.latest;
  // An API from before item J sends no `expect`: nothing is asked then.
  const expect = data.expect ?? NO_EXPECT;
  const sub = report
    ? t("monitoring.reporter.lastReport", { when: fmtDateTime(report.receivedAt), version: report.pluginVersion })
    : t("monitoring.reporter.sub");

  const actions = (
    <>
      <StatusPill tone={linkTone(state)}>{t(`monitoring.reporter.state.${state}`)}</StatusPill>
      {canManage && allowed && state === "none" ? (
        <Btn small tone="dark" disabled={busy} onClick={() => void issue()}>
          {busy ? <Loader2 className="size-[15px] animate-spin" /> : <KeyRound className="size-[15px]" strokeWidth={1.8} />}
          {t("monitoring.reporter.create")}
        </Btn>
      ) : null}
      {canManage && state !== "none" ? (
        <>
          {allowed ? (
            <Btn small disabled={busy} onClick={() => setAsking("rotate")}>
              <KeyRound className="size-[15px]" strokeWidth={1.8} />
              {t("monitoring.reporter.rotate")}
            </Btn>
          ) : null}
          <Btn small tone="danger" disabled={busy} onClick={() => setAsking("revoke")}>
            <Unplug className="size-[15px]" strokeWidth={1.8} />
            {t("monitoring.reporter.revoke")}
          </Btn>
        </>
      ) : null}
    </>
  );

  return (
    <section className="crm-card mb-4">
      <CardHead title={title} sub={sub} actions={actions} />
      {body ? (
        <p className={state === "silent" ? "px-[26px] pb-4 text-[13px] text-[var(--color-destructive)]" : "crm-sub px-[26px] pb-4 text-[13px]"}>
          {body}
        </p>
      ) : null}
      {!allowed && state !== "none" ? (
        <p className="crm-sub px-[26px] pb-4 text-[13px]">{t("monitoring.reporter.notInPlanKept")}</p>
      ) : null}
      {report && !report.hostMatches ? (
        <p className="px-[26px] pb-4 text-[13px] text-[var(--color-destructive)]">
          {t("monitoring.reporter.hostMismatch", { url: report.homeUrl })}
        </p>
      ) : null}
      {report ? (
        <>
          <ReportSummary report={report} expect={expect} t={t} />
          <ReportTabs report={report} history={data.history} expect={expect} t={t} />
        </>
      ) : null}

      {token ? <TokenDialog token={token} t={t} onClose={() => setToken(null)} /> : null}
      {asking === "rotate" ? (
        <ConfirmModal
          title={t("monitoring.reporter.rotateTitle")}
          body={t("monitoring.reporter.rotateBody")}
          confirmLabel={t("monitoring.reporter.rotate")}
          cancelLabel={t("common.cancel")}
          tone="dark"
          onConfirm={async () => {
            setAsking(null);
            await issue();
          }}
          onClose={() => setAsking(null)}
        />
      ) : null}
      {asking === "revoke" ? (
        <ConfirmModal
          title={t("monitoring.reporter.revokeTitle")}
          body={t("monitoring.reporter.revokeBody")}
          confirmLabel={t("monitoring.reporter.revoke")}
          cancelLabel={t("common.cancel")}
          onConfirm={async () => {
            await revoke();
            setAsking(null);
          }}
          onClose={() => setAsking(null)}
        />
      ) : null}
    </section>
  );
}
